import { Button, Grid, makeStyles } from "@material-ui/core";
import NotificationIcon from "@material-ui/icons/Notifications";
import NotificationOffIcon from "@material-ui/icons/NotificationsOff";
import { useEffect, useMemo, useState } from "react";
import { IPerformanceProFilterOptions } from "../../../store/GlobalContext";
import { EggUnit } from "../../../utility/EggUnitUtility";
import { numberOfEggsInACase, numberOfEggsInADozen } from "../../../utility/EggUtility";
import { t } from "../../../utility/TranslateUtility";
import { IAlarm } from "../../Alarms/AddOrUpdateAlarm/Common";
import { AlarmCondition, AlarmStatus, AlarmTopic } from "../../Alarms/Types";
import { ChartCard } from "../../ProductionViews/Common";
import { TargetsView } from "../../Settings/Targets/Types";
import BarChartDisplayTarget, { HasTargets } from "../BarChartDisplayTarget";
import BarChartDisplayUnit, { HasUnits } from "../BarChartDisplayUnits";
import { ChartMapping } from "../ChartMapping";
import PeriodViewBarChart from "../PeriodViewBarChart";
import { KpiMeasureType, PerformancePeriod, PerformancePeriodSummation, ValueType } from "../Types";
import { PerformanceProChartTitle } from "./PerformanceProChartTitle";
import { colors } from "../../../colors";

interface Props {
  chartMapping: ChartMapping;
  options: IPerformanceProFilterOptions;
  summation: PerformancePeriodSummation;
  periods: PerformancePeriod[] | never[];
  valueType: ValueType;
  targets: TargetsView | null;
  hasAlarmSet: boolean;
  showAlarm: boolean;
  alarm: IAlarm | undefined;
  onShowAlarmPopupClick: (showDialog: boolean) => void;
  awaitingResponse: boolean;
  showAbsoluteValues: boolean
  toggleShowAbsoluteClickHandler: (value: boolean) => void  
}

const CanBeAbsoluteOrPercentile : KpiMeasureType[] = [
  KpiMeasureType.TableEggs,
  KpiMeasureType.InputOffgrades,
  KpiMeasureType.Upgrades,
  KpiMeasureType.Outputoffgrades,
  KpiMeasureType.Bypass,
  KpiMeasureType.BucketEggs
]
const ShouldShowAbsoluteOrPercentileToggle = (measure: KpiMeasureType) : boolean => CanBeAbsoluteOrPercentile.includes(measure);

const useStyles = makeStyles((theme: any) => {
  const btnCommon = {
    width: "40px !important",
    height: "40px !important",
    minWidth: "0 !important",
    minHeight: "0 !important",
  };
  
  return {
    targetsNodeContainer: {
      paddingBottom: theme.spacing(2),
    },
    floatRight: {
      display: "flex",
      justifyContent: "flex-end",
    },
    percentageBtn: {
      ...btnCommon,
      backgroundColor: colors.crackBlue,
      color: colors.white,
      "&:hover": {
        backgroundColor: colors.crackBlue,
        color: colors.white,
      },
    },
    numberBtn: {
      ...btnCommon,
      backgroundColor: colors.grassGreen,
      color: colors.white,
      "&:hover": {
        backgroundColor: colors.grassGreen,
        color: colors.white,
      },
    },
  }
});

export function PerformanceProViewPresentation({
  chartMapping,
  options,
  summation,
  periods,
  valueType,
  targets,
  hasAlarmSet,
  showAlarm,
  alarm,
  onShowAlarmPopupClick,
  awaitingResponse,
  showAbsoluteValues,
  toggleShowAbsoluteClickHandler,
}: Props) {
  const classes = useStyles();
  const [alarmData, setAlarmData] = useState<any>();
  const chartEggUnit = chartMapping.useEggUnit ? getEggUnit(targets) : null;
  const targetEggUnit = getEggUnit(targets);

  const targetsNode = useMemo(() => {
    return HasTargets(chartMapping.performanceProChart, options, targets) ? (
      <BarChartDisplayTarget
        measure={chartMapping.performanceProChart}
        targets={targets!}
        selectedEggType={options.selectedEggType}
      />
    ) : undefined;
  }, [chartMapping.performanceProChart, options, targets]);

  useEffect(() => {
    if (
      showAlarm &&
      (alarm?.status.toString() === AlarmStatus[AlarmStatus.Active] ||
        Number(alarm?.status) === 0)
    ) {
      const data = periods
        .filter((p) => p !== undefined)
        .map((p: any) => ({
          x: p.dateTime,
          y: calculateAlarmLevel(
            alarm?.topic,
            alarm?.level || 0,
            targets?.units.eggUnit || EggUnit.Eggs
          ),
        }));
      setAlarmData([
        {
          type: "area",
          data: data,
          fillColor: "#5c9cd533",
          threshold:
            alarm?.condition.toString() ===
              AlarmCondition[AlarmCondition.Above] ||
            Number(alarm?.condition) === 1
              ? Infinity
              : 0,
          zIndex: -2,
          marker: {
            enabled: false,
          },
          ...additionalChartProps,
        },
        {
          type: "line",
          name: "alarm threshhold",
          data: data,
          dashStyle: "longdash",
          color: "#5c9cd5",
          lineWidth: 5,
          zIndex: 2,
          marker: {
            enabled: false,
          },
          ...additionalChartProps,
        },
      ]);
    } else {
      setAlarmData(undefined);
    }
  }, [alarm, showAlarm, hasAlarmSet, periods]);

  return (
    <ChartCard
      headerTitle={t(chartMapping.headerTitle)}
      isLoading={awaitingResponse}
    >
      {periods && (
        <>
          <Grid container>
            <Grid item xs={10}>
              <PerformanceProChartTitle
                summation= { showAbsoluteValues ? summation.numerator! : summation.value }
                valueType= { valueType }
                eggUnit= { targetEggUnit }
              />
              {targetsNode && (
                <div className={classes.targetsNodeContainer}>
                  {targetsNode}
                </div>
              )}
              { HasUnits(chartMapping.performanceProChart) && (
                <div className={classes.targetsNodeContainer}>
                    <BarChartDisplayUnit
                        summation={showAbsoluteValues ? summation.value : summation.numerator!}
                        eggUnit={targetEggUnit}
                        isPercentage={showAbsoluteValues}
                    />
                </div>
              )}
            </Grid>

            <Grid item xs={2} className={classes.floatRight}>
              <div>
                <Button onClick={() => onShowAlarmPopupClick(true)}>
                  {hasAlarmSet &&
                    (alarm?.status === AlarmStatus.Active ||
                      alarm?.status.toString() === "Active") && (
                      <NotificationIcon />
                    )}
                  {(!hasAlarmSet ||
                    alarm?.status.toString() === "Passive" ||
                    alarm?.status === AlarmStatus.Passive) && (
                    <NotificationOffIcon />
                  )}
                </Button>
                { ShouldShowAbsoluteOrPercentileToggle(chartMapping.performanceProChart) &&
                  <Button
                    className={ showAbsoluteValues ? classes.numberBtn : classes.percentageBtn }
                    onClick={() => toggleShowAbsoluteClickHandler(!showAbsoluteValues) }
                    size="small"
                  >   
                    { showAbsoluteValues ? "#" : "%" }
                  </Button>
                }
              </div>
            </Grid>
          </Grid>

          <PeriodViewBarChart
            periods={periods}
            options={options}
            chartMapping={chartMapping}
            eggUnit={chartEggUnit}
            additionalData={alarmData}
            showAbsoluteValuesOverride={ showAbsoluteValues }
          />
        </>
      )}
    </ChartCard>
  );
}

function getEggUnit(targets: TargetsView | null): EggUnit | null {
  return targets?.units.eggUnit ?? null;
}

const additionalChartProps = {
  enableMouseTracking: false,
  states: {
    inactive: {
      opacity: 1,
    },
  },
};

function calculateAlarmLevel(
  topic: any,
  level: number,
  eggUnit: string
): number {
  switch (topic) {
    case "TotalEggs":
    case "Throughput":
    case AlarmTopic.TotalEggs:
    case AlarmTopic.Throughput:
      switch (eggUnit) {
        case EggUnit.Dozens:
          return level / numberOfEggsInADozen;
        case EggUnit.Cases:
          return level / numberOfEggsInACase;
      }
      break;
  }
  return level;
}
